import type { OcrRegion } from "./types";

const coordinate = (value: number) => value.toFixed(6);

function sharedContourKey(region: OcrRegion): string | null {
  if (!region.polygon || region.polygon.length < 3) return null;
  return [
    region.pageId,
    coordinate(region.x),
    coordinate(region.y),
    coordinate(region.width),
    coordinate(region.height),
    region.polygon.map((point) => `${coordinate(point.x)},${coordinate(point.y)}`).join(";")
  ].join("|");
}

function area(region: Pick<OcrRegion, "width" | "height">): number {
  return region.width * region.height;
}

function overlapOfSmallerRegion(
  left: Pick<OcrRegion, "x" | "y" | "width" | "height">,
  right: Pick<OcrRegion, "x" | "y" | "width" | "height">
): number {
  const width = Math.max(0, Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x));
  const height = Math.max(0, Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y));
  const smallerArea = Math.min(area(left), area(right));
  return smallerArea > 0 ? width * height / smallerArea : 0;
}

/**
 * Older detector passes can preserve both a loose speech-bubble rectangle and
 * a tight text rectangle for the same OCR result. Exact text plus near-total
 * containment is strong enough evidence that they are one target even when a
 * traced contour was unavailable; ordinary neighbouring bubbles remain
 * separate because neither rectangle substantially contains the other.
 */
export function isSameContainedTextTarget(
  left: Pick<OcrRegion, "pageId" | "x" | "y" | "width" | "height">,
  right: Pick<OcrRegion, "pageId" | "x" | "y" | "width" | "height">,
  leftText: string,
  rightText: string
): boolean {
  return left.pageId === right.pageId &&
    leftText === rightText &&
    overlapOfSmallerRegion(left, right) >= 0.9;
}

function isSameUnshapedTarget(left: OcrRegion, right: OcrRegion): boolean {
  return isSameContainedTextTarget(
    left,
    right,
    left.correctedText ?? left.text,
    right.correctedText ?? right.text
  );
}

function isSamePresentedTarget(left: OcrRegion, right: OcrRegion): boolean {
  const leftContour = sharedContourKey(left);
  const rightContour = sharedContourKey(right);
  return Boolean(leftContour && leftContour === rightContour) || isSameUnshapedTarget(left, right);
}

/**
 * A reindex can preserve multiple learned lines while upgrading them to the
 * same recovered speech-bubble contour. Present those rows as one target while
 * retaining their individual IDs, tokens, translations, and notes.
 */
export function groupRegionsByContour(regions: OcrRegion[]): OcrRegion[][] {
  const groups: OcrRegion[][] = [];
  const indexes = new Map<string, number>();
  for (const region of regions) {
    const duplicateIndex = groups.findIndex((group) => group.some((candidate) => isSameUnshapedTarget(candidate, region)));
    if (duplicateIndex !== -1) {
      groups[duplicateIndex].push(region);
      continue;
    }
    const key = sharedContourKey(region);
    if (!key) {
      groups.push([region]);
      continue;
    }
    const existingIndex = indexes.get(key);
    if (existingIndex === undefined) {
      indexes.set(key, groups.length);
      groups.push([region]);
    } else {
      groups[existingIndex].push(region);
    }
  }
  return groups;
}

export function contourGroupForRegion(regions: OcrRegion[], selected: OcrRegion): OcrRegion[] {
  return groupRegionsByContour(regions).find((group) => group.some((region) => region.id === selected.id)) ?? [selected];
}

/**
 * Details keeps distinct OCR lines that share one physical bubble, but an
 * idempotency failure must not turn one identical row into repeated dialogue.
 * Prefer the active row when a deep link or user action selected one of the
 * duplicates so its correction and learning actions remain addressable.
 */
export function deduplicateContourText(
  regions: OcrRegion[],
  preferredRegionId?: string
): OcrRegion[] {
  const selected: OcrRegion[] = [];
  for (const region of regions) {
    const existingIndex = selected.findIndex((candidate) =>
      isSamePresentedTarget(candidate, region) &&
      (candidate.correctedText ?? candidate.text) === (region.correctedText ?? region.text)
    );
    if (existingIndex === -1) {
      selected.push(region);
      continue;
    }
    if (region.id === preferredRegionId) {
      selected[existingIndex] = region;
    }
  }
  return selected;
}
